import { deepMerge } from "@tellescope/utils";
import type { ModelName, MutationMethodForModel } from "../../types";
import { AUTOMATIC_MUTATION_SIDE_EFFECT_CONFIGS } from "./automatic-mutation-side-effect-configs";
import { getCommonMutationSideEffectConfigs } from "./common-mutation-side-effect-configs";
import { SideEffectConfigConstructor } from "./types";

/**
 * Retrieves the configured side effects from AUTOMATIC_MUTATION_SIDE_EFFECT_CONFIGS for a given model and mutation method.
 *
 * @template Model - The Tellescope model name (e.g., "users", "appointments")
 * @template Method - The mutation method for the model (e.g., "create_user", "update_appointment")
 *
 * @param args.model - The Tellescope model name
 * @param args.method - The mutation method to retrieve side effects for
 */
export const getMutationSideEffectUpdaters = <Model extends ModelName, Method extends MutationMethodForModel<Model>>({
  model,
  method,
}: {
  model: Model;
  method: Method;
}): SideEffectConfigConstructor<Model, Method> | undefined => {
  const customConfigConstructor = AUTOMATIC_MUTATION_SIDE_EFFECT_CONFIGS[model]?.[method];
  const commonConfigConstructor = getCommonMutationSideEffectConfigs<Model>()[method];

  const result: SideEffectConfigConstructor<Model, Method> = (request, response, context) => {
    const customConfigs = customConfigConstructor?.(request, response, context);
    const commonConfigs = commonConfigConstructor?.(request, response, context);

    return deepMerge(commonConfigs ?? {}, customConfigs ?? {});
  };

  return result;
};
